import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Icon,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Modal,
    Typography,
    useMediaQuery,
    Fade,
    Slide,
    Backdrop,
    Divider,
    Paper,
    IconButton,
    Zoom
} from "@mui/material";
import { useState, useEffect } from "react";
import { obtenerNombreInforme } from "@/utils/informeUtils";
import { useBorrarCarritoMutation } from "../../redux/api/informeApi";
import { ICarrito, IInforme } from "../../redux/models/typesInformes";
import { useAppSelector } from "../../redux/store";
import MyToastShow from "../Layout/MyToast";

type ModalBorrarCarritoProps = {
    open: boolean;
    onCancel: () => void;
    carrito?: ICarrito;
    informes?: IInforme[];
    onCarritoBorrado: (carritoActualizado: ICarrito) => void;
};
const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 600,
    maxHeight: "90vh",
    overflow: "hidden",
    borderRadius: 3,
    outline: "none",
};

export type LiquidarInformesArgs = {
    listaDevuelta: number[];
    bool: boolean;
};

const ModalBorrarCarrito = ({
    carrito,
    informes = [],
    open,
    onCancel,
    onCarritoBorrado,
}: ModalBorrarCarritoProps) => {
    const darkMode = useAppSelector((state) => state.theme.darkTheme);
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));

    const [borrarCarrito] = useBorrarCarritoMutation();
    const [loading, setLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (!open) {
            setLoading(false);
            setIsDeleting(false);
            setDeleteProgress(false);
            setShowSuccess(false);
        }
    }, [open]);

    const handleLiquidar = async () => {
        if (!carrito) return;

        setLoading(true);
        setIsDeleting(true);

        // Add a small delay to show the deleting animation
        setTimeout(() => {
            setDeleteProgress(true);
        }, 500);

        try {
            await borrarCarrito(carrito.idCarrito).unwrap();

            // Show success animation
            setShowSuccess(true);
            
            setTimeout(() => {
                onCarritoBorrado(carrito);
                onCancel();
            }, 800);

        } catch (err) {
            setIsDeleting(false);
            setDeleteProgress(false);
            MyToastShow({ message: "Error al eliminar el carrito", type: "error" });
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 600);
        }
    };



    return (
        <Modal
            open={open}
            onClose={!isDeleting ? onCancel : undefined}
            aria-labelledby={`borrar-carrito-${carrito?.idCarrito}`}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 700,
                sx: {
                    backdropFilter: "blur(4px)",
                    background: "rgba(0,0,0,0.6)"
                }
            }}
            sx={{ zIndex: 1300 }}
        >
            <Fade in={open} timeout={700}>
                <Paper
                    elevation={24}
                    sx={(theme) => ({
                        ...style,
                        bgcolor: darkMode ? "#2d2d2d" : "#fff",
                        border: darkMode ? "1px solid #404040" : "1px solid #e0e0e0",
                        transform: isDeleting
                            ? "translate(-50%, -50%) scale(0.98)"
                            : "translate(-50%, -50%) scale(1)",
                        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        opacity: deleteProgress ? 0.8 : 1,
                        filter: deleteProgress ? "blur(1px)" : "none"
                    })}
                    aria-label="borrar-carrito-container"
                >
                    {/* Success Overlay */}
                    {showSuccess && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: "rgba(76, 175, 80, 0.95)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 3,
                                zIndex: 10,
                                animation: "fadeInSuccess 0.5s ease-in-out",
                                "@keyframes fadeInSuccess": {
                                    "0%": { opacity: 0, transform: "scale(0.8)" },
                                    "100%": { opacity: 1, transform: "scale(1)" }
                                }
                            }}
                        >
                            <Box sx={{ textAlign: "center", color: "white" }}>
                                <Zoom in={showSuccess} timeout={600}>
                                    <Icon sx={{ fontSize: 64, mb: 2 }}>check_circle</Icon>
                                </Zoom>
                                <Typography variant="h5" fontWeight="bold">
                                    ¡Carrito eliminado!
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                    La operación se completó exitosamente
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {/* Header */}
                    <Box
                        sx={{
                            p: 3,
                            pb: 2,
                            background: darkMode
                                ? "linear-gradient(135deg, #e57373 0%, #ef5350 100%)"
                                : "linear-gradient(135deg, #ff7961 0%, #f44336 100%)",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Zoom in={open} timeout={600}>
                                <Icon
                                    sx={{
                                        fontSize: 32,
                                        animation: isDeleting ? "pulse 1s infinite" : "none",
                                        "@keyframes pulse": {
                                            "0%": { transform: "scale(1)" },
                                            "50%": { transform: "scale(1.1)" },
                                            "100%": { transform: "scale(1)" }
                                        }
                                    }}
                                >
                                    delete_forever
                                </Icon>
                            </Zoom>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                    opacity: deleteProgress ? 0.7 : 1,
                                    transition: "opacity 0.3s ease"
                                }}
                            >
                                Eliminar Carrito
                            </Typography>
                        </Box>

                        {!isDeleting && (
                            <IconButton
                                onClick={onCancel}
                                sx={{
                                    color: "white",
                                    "&:hover": {
                                        bgcolor: "rgba(255,255,255,0.1)",
                                        transform: "rotate(90deg)"
                                    },
                                    transition: "all 0.2s ease"
                                }}
                            >
                                <Icon>close</Icon>
                            </IconButton>
                        )}
                    </Box>

                    {/* Content */}
                    <Box sx={{
                        p: 3,
                        opacity: showSuccess ? 0 : 1,
                        transition: "opacity 0.3s ease"
                    }}>
                        <Slide direction="up" in={open && !showSuccess} timeout={600}>
                            <Box>
                                <Typography
                                    variant="h6"
                                    textAlign="center"
                                    sx={{
                                        mb: 3,
                                        color: darkMode ? "#fff" : "#333",
                                        opacity: deleteProgress ? 0.5 : 1,
                                        transition: "opacity 0.3s ease"
                                    }}
                                >
                                    ¿Estás seguro de que deseas eliminar el carrito{" "}
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: "bold",
                                            color: darkMode ? "#ffab91" : "#ff7043"
                                        }}
                                    >
                                        #{carrito?.idCarrito}
                                    </Box>
                                    ?
                                </Typography>

                                <Typography
                                    variant="body2"
                                    textAlign="center"
                                    sx={{
                                        mb: 3,
                                        color: darkMode ? "#bbb" : "#666",
                                        opacity: deleteProgress ? 0.5 : 1,
                                        transition: "opacity 0.3s ease"
                                    }}
                                >
                                    Esta acción eliminará permanentemente todos los informes del carrito.
                                </Typography>

                                <Divider sx={{ mb: 2 }} />

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1,
                                        fontWeight: "bold",
                                        color: darkMode ? "#fff" : "#333",
                                        opacity: deleteProgress ? 0.5 : 1,
                                        transition: "opacity 0.3s ease"
                                    }}
                                >
                                    Informes a eliminar ({informes.length}):
                                </Typography>

                                <List
                                    sx={{
                                        width: "100%",
                                        maxHeight: 300,
                                        overflow: "auto",
                                        border: darkMode ? "1px solid #404040" : "1px solid #e0e0e0",
                                        borderRadius: 2,
                                        bgcolor: darkMode ? "#1a1a1a" : "#f9f9f9",
                                        "& .MuiListItem-root": {
                                            borderBottom: darkMode ? "1px solid #333" : "1px solid #eee",
                                            "&:last-child": {
                                                borderBottom: "none"
                                            }
                                        }
                                    }}
                                >
                                    {open && informes.length === 0 ? (
                                        <ListItem>
                                            <ListItemText
                                                primary="No hay informes en este carrito"
                                                sx={{
                                                    textAlign: "center",
                                                    fontStyle: "italic",
                                                    color: darkMode ? "#999" : "#666"
                                                }}
                                            />
                                        </ListItem>
                                    ) : (
                                        open && informes.map((informe, index) => (
                                            <Slide
                                                key={`${informe.id}-${index}`}
                                                direction="right"
                                                in={open && !deleteProgress}
                                                timeout={400 + (index * 150)}
                                            >
                                                <ListItem
                                                    sx={{
                                                        py: 1.5,
                                                        "&:hover": {
                                                            bgcolor: darkMode ? "#333" : "#f0f0f0"
                                                        },
                                                        transition: "all 0.3s ease",
                                                        opacity: deleteProgress ? 0.3 : 1,
                                                        transform: deleteProgress ? "translateX(-20px)" : "translateX(0)",
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        <Icon sx={{
                                                            color: darkMode ? "#ffab91" : "#ff7043",
                                                            fontSize: 20
                                                        }}>
                                                            description
                                                        </Icon>
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={obtenerNombreInforme(informe)}
                                                        secondary={`ID: ${informe.id}`}
                                                        sx={{
                                                            "& .MuiTypography-root": {
                                                                color: darkMode ? "#fff" : "#333"
                                                            },
                                                            "& .MuiListItemText-secondary": {
                                                                color: darkMode ? "#999" : "#666",
                                                                fontSize: "0.75rem"
                                                            }
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            bgcolor: darkMode ? "#ffab91" : "#ff7043",
                                                            opacity: deleteProgress ? 0 : 0.7,
                                                            transition: "opacity 0.3s ease"
                                                        }}
                                                    />
                                                </ListItem>
                                            </Slide>
                                        ))
                                    )}
                                </List>
                            </Box>
                        </Slide>
                    </Box>

                    {/* Actions */}
                    <Box
                        sx={{
                            p: 3,
                            pt: 0,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                            transform: deleteProgress ? "translateY(10px)" : "translateY(0)",
                            opacity: deleteProgress ? 0.5 : 1,
                            transition: "all 0.3s ease"
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onCancel}
                            disabled={isDeleting}
                            sx={{
                                minWidth: 100,
                                borderColor: darkMode ? "#666" : "#ccc",
                                color: darkMode ? "#fff" : "#333",
                                "&:hover": {
                                    borderColor: darkMode ? "#888" : "#aaa",
                                    transform: "translateY(-1px)"
                                },
                                transition: "all 0.2s ease"
                            }}
                        >
                            Cancelar
                        </Button>

                        <LoadingButton
                            loading={loading}
                            variant="contained"
                            color="error"
                            onClick={handleLiquidar}
                            disabled={isDeleting}
                            sx={{
                                minWidth: 120,
                                bgcolor: "#ff7043",
                                "&:hover": {
                                    bgcolor: "#f4511e",
                                    transform: !loading ? "translateY(-1px)" : "none",
                                    boxShadow: !loading ? "0 4px 12px rgba(255, 112, 67, 0.4)" : "none"
                                },
                                "&:disabled": {
                                    bgcolor: "#666"
                                },
                                transition: "all 0.2s ease"
                            }}
                            loadingPosition="start"
                            startIcon={<Icon>delete</Icon>}
                        >
                            {isDeleting ? "Eliminando..." : "Eliminar"}
                        </LoadingButton>
                    </Box>
                </Paper>
            </Fade>
        </Modal>
    );
};

export default ModalBorrarCarrito;
